import React from "react";
import styled, { css } from "styled-components";

import { ReactComponent as ProIconSVG } from "../../Assets/Avatar/Icons/pro.svg";
import { ReactComponent as PlatinumIconSVG } from "../../Assets/Avatar/Icons/platinum.svg";
import { ReactComponent as PartnerIconSVG } from "../../Assets/Avatar/Icons/partner.svg";
import { ReactComponent as SuperstarIconSVG } from "../../Assets/Avatar/Icons/superstar.svg";
import { ReactComponent as ProPlayerIconSVG } from "../../Assets/Avatar/Icons/pro-player.svg";
import { ReactComponent as InternalIconSVG } from "../../Assets/Avatar/Icons/internal.svg";
import { ReactComponent as EssentialsIconSVG } from "../../Assets/Avatar/Icons/essentials.svg";

import { TUserGroup, TUserRank } from "MVVM/@types";
import { AnimatedTooltip } from "../AnimatedTooltip";
import { getUserGroupStyle } from "MVVM/Utilities/User";
import { getEffectiveUserGroup } from "../AvatarV2";

interface UserBadgeProps {
  userGroup: TUserGroup;
  rank?: TUserRank;
  size?: "xs" | "sm" | "base" | "md" | "lg" | "xl";
  className?: string;
  style?: React.CSSProperties;
}

const getBadgeSize = (size: string) => {
  switch (size) {
    case "xs":
      return "12px";
    case "sm":
      return "14px";
    case "base":
    case "md":
      return "18px";
    case "lg":
      return "36px";
    case "xl":
      return "72px";
    default:
      return "18px";
  }
};

const iconStyle = css<{ size: string }>`
  cursor: pointer;
  height: ${({ size }) => getBadgeSize(size)};
  width: ${({ size }) => getBadgeSize(size)};
`;

const StyledProIcon = styled(ProIconSVG)<{ size: string }>`
  ${iconStyle}
`;

const StyledPlatinumIcon = styled(PlatinumIconSVG)<{ size: string }>`
  ${iconStyle}
`;

const StyledPartnerIcon = styled(PartnerIconSVG)<{ size: string }>`
  ${iconStyle}
`;

const StyledSuperstarIcon = styled(SuperstarIconSVG)<{ size: string }>`
  ${iconStyle}
`;

const StyledProPlayerIcon = styled(ProPlayerIconSVG)<{ size: string }>`
  ${iconStyle}
`;

const StyledInternalIcon = styled(InternalIconSVG)<{ size: string }>`
  ${iconStyle}
`;

const StyledEssentialIcon = styled(EssentialsIconSVG)<{ size: string }>`
  ${iconStyle}
`;

const BadgeContainer = styled.div<{ size: string }>`
  align-items: center;
  display: inline-flex;
  filter: drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.4));
  height: ${({ size }) => getBadgeSize(size)};
  justify-content: center;
  width: ${({ size }) => getBadgeSize(size)};
`;

export const UserBadge = ({
  userGroup,
  rank,
  size = "base",
  className,
  style,
}: UserBadgeProps) => {
  const renderIcon = (userGroup: TUserGroup) => {
    switch (userGroup) {
      case "PRO":
        return <StyledProIcon size={size} />;
      case "CREATOR_PLAT":
        return <StyledPlatinumIcon size={size} />;
      case "PARTNER":
        return <StyledPartnerIcon size={size} />;
      case "INTERNAL":
        return <StyledInternalIcon size={size} />;
      case "SUPERSTAR":
        return <StyledSuperstarIcon size={size} />;
      case "PRO_PLAYER":
        return <StyledProPlayerIcon size={size} />;
      case "ESSENTIALS":
        return <StyledEssentialIcon size={size} />;
      default:
        return null;
    }
  };

  const effectiveUserGroup = getEffectiveUserGroup(userGroup, rank);
  const groupStyle = getUserGroupStyle(effectiveUserGroup);

  const icon = renderIcon(effectiveUserGroup);

  if (!icon) return null;

  return (
    <AnimatedTooltip position="top" content={groupStyle.display}>
      <BadgeContainer size={size} className={className} style={style}>
        {icon}
      </BadgeContainer>
    </AnimatedTooltip>
  );
};
